import {
  Brain,
  Users,
  Zap,
  FileCheck,
  Sparkles,
  DollarSign,
  ShieldCheck,
  Globe,
  Clock
} from 'lucide-react'

const features = [
  {
    name: 'AI-Powered Intelligence',
    description: 'Accelerate assessments, identify control gaps, generate audit-ready documentation, and surface risks before they become findings.',
    icon: Brain,
    highlight: true,
  },
  {
    name: 'Built by Practitioners',
    description: 'Our team has led incident response engagements, conducted forensic investigations, and helped organizations meet the demands of auditors.',
    icon: Users,
  },
  {
    name: 'Speed at Scale',
    description: 'Globally distributed edge architecture powered by Cloudflare Workers delivers sub-second response times worldwide.',
    icon: Zap,
  },
  {
    name: 'Frameworks Built In',
    description: 'NIST CSF, CIS Controls, SOC 2, ISO 27001, HIPAA, PCI DSS, CMMC—ready out of the box, pre-mapped and cross-referenced.',
    icon: FileCheck,
  },
  {
    name: 'Powerful Simplicity',
    description: 'Advanced capabilities through an interface your team can actually use on day one. No six-month implementation required.',
    icon: Sparkles,
  },
  {
    name: 'Transparent Pricing',
    description: 'No hidden fees. No per-module charges. No surprise invoices. Straightforward pricing that scales with your organization.',
    icon: DollarSign,
  },
]

const capabilities = [
  {
    title: 'Automated Evidence Collection',
    description: 'Connect your tools once, collect evidence continuously',
    icon: ShieldCheck,
  },
  {
    title: 'Continuous Monitoring',
    description: 'Real-time visibility across your security program',
    icon: Globe,
  },
  {
    title: 'Reduce Audit Prep by 80%',
    description: 'From months of preparation to days of validation',
    icon: Clock,
  },
]

export default function Features() {
  return (
    <section id="features" className="py-20 sm:py-28 bg-white">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-base font-semibold leading-7 text-accent-teal">
            Why Clarantis
          </h2>
          <p className="mt-2 text-3xl font-bold tracking-tight text-primary-700 sm:text-4xl">
            Everything you need to manage compliance
          </p>
          <p className="mt-6 text-lg leading-8 text-navy-500">
            Modern GRC solutions designed for enterprise teams who need to move fast without compromising security.
          </p>
        </div>

        {/* Main feature grid */}
        <div className="mx-auto mt-16 max-w-7xl">
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((feature) => (
              <div
                key={feature.name}
                className={`relative rounded-2xl p-8 transition-all duration-200 ${
                  feature.highlight
                    ? 'bg-gradient-to-br from-primary-700 to-primary-600 text-white shadow-xl ring-1 ring-primary-700/10'
                    : 'bg-navy-50 hover:bg-navy-100 hover:shadow-md'
                }`}
              >
                <div className={`inline-flex items-center justify-center rounded-xl p-3 ${
                  feature.highlight
                    ? 'bg-white/10'
                    : 'bg-primary-700/10'
                }`}>
                  <feature.icon
                    className={`h-6 w-6 ${
                      feature.highlight ? 'text-accent-teal' : 'text-primary-600'
                    }`}
                    aria-hidden="true"
                  />
                </div>
                <h3 className={`mt-6 text-lg font-semibold leading-7 ${
                  feature.highlight ? 'text-white' : 'text-primary-700'
                }`}>
                  {feature.name}
                </h3>
                <p className={`mt-2 text-base leading-7 ${
                  feature.highlight ? 'text-primary-100' : 'text-navy-500'
                }`}>
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Capabilities highlight bar */}
        <div className="mt-20 rounded-2xl bg-navy-50 p-8 sm:p-12">
          <div className="mx-auto max-w-2xl text-center mb-10">
            <h3 className="text-2xl font-bold tracking-tight text-primary-700">
              Enterprise-Grade Capabilities
            </h3>
            <p className="mt-3 text-navy-500">
              Built for organizations with real compliance obligations
            </p>
          </div>
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-3">
            {capabilities.map((capability) => (
              <div key={capability.title} className="text-center">
                <div className="mx-auto inline-flex items-center justify-center rounded-full bg-white p-4 shadow-md ring-1 ring-navy-100">
                  <capability.icon className="h-6 w-6 text-accent-teal" aria-hidden="true" />
                </div>
                <h4 className="mt-4 text-base font-semibold text-primary-700">
                  {capability.title}
                </h4>
                <p className="mt-2 text-sm text-navy-500">
                  {capability.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Trust indicators */}
        <div className="mt-16 text-center">
          <p className="text-sm font-medium text-navy-400 uppercase tracking-wider mb-6">
            Trusted by security teams at leading organizations
          </p>
          <div className="flex flex-wrap justify-center items-center gap-8 sm:gap-12">
            {/* Placeholder trust logos - can be replaced with actual client logos */}
            <div className="h-10 w-24 bg-navy-100 rounded flex items-center justify-center text-navy-400 text-xs font-medium">
              Enterprise
            </div>
            <div className="h-10 w-24 bg-navy-100 rounded flex items-center justify-center text-navy-400 text-xs font-medium">
              Financial
            </div>
            <div className="h-10 w-24 bg-navy-100 rounded flex items-center justify-center text-navy-400 text-xs font-medium">
              Healthcare
            </div>
            <div className="h-10 w-24 bg-navy-100 rounded flex items-center justify-center text-navy-400 text-xs font-medium">
              Technology
            </div>
            <div className="h-10 w-24 bg-navy-100 rounded flex items-center justify-center text-navy-400 text-xs font-medium">
              Manufacturing
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
